import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { useRecoilValue } from 'recoil';
import { FlexColumn, Flex, FlexCenter, Loader, card } from '@ubeya/shared-web/components';

import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { roundTimeToNearest } from '@ubeya/shared/utils/time';
import useInterval from '@ubeya/shared/hooks/useInterval';
import { EmployeeCard } from 'shared/components';
import { schedulingHourlyLightView } from 'shared/atoms/scheduling';
import EmployeeTimeAvailabilityModal from 'scheduling/components/WeekView/EmployeeTimeAvailabilityModal';
import BookedRenderer from 'scheduling/components/WeekView/CellRenderers/HourlyEmployeesRenderer/BookedRenderer';
import TimeOffRenderer from 'scheduling/components/WeekView/CellRenderers/HourlyEmployeesRenderer/TimeOffRenderer';
import GeneralAvailabilityRenderer from 'scheduling/components/WeekView/CellRenderers/HourlyEmployeesRenderer/GeneralAvailabilityRenderer';
import BreakPlotsRenderer from 'scheduling/components/WeekView/CellRenderers/HourlyEmployeesRenderer/BreakPlotsRenderer';

const CELL_WIDTH = 40;

const Wrapper = styled(FlexColumn)`
  ${card}
  flex: 1;

  opacity: ${({ isLoading }) => (isLoading ? 0.3 : 1)};
  width: 100%;
  height: 100%;

  .daily-grid-cell {
    width: ${CELL_WIDTH}px;
    min-width: ${CELL_WIDTH}px;
    max-width: ${CELL_WIDTH}px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  }

  .daily-left-cell {
    position: sticky;
    border-right: 1px solid ${({ theme }) => theme.colors.grey2};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
    background-color: ${({ theme }) => theme.colors.white};
    left: 0;
  }
`;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: auto;
  width: 100%;
  height: 100vh;
`;

const LoaderWrapper = styled(FlexCenter)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const Row = styled(Flex)`
  position: relative;
  height: max-content;
  width: 100%;

  ${({ isFull }) => isFull && 'flex: 1;'};
`;

const Cell = styled.div`
  flex: 1;

  ${({ header }) =>
    !header &&
    css`
      border-left: 1px solid ${({ theme }) => theme.colors.grey2};
    `}
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns, gridInterval }) => `250px repeat(${columns * gridInterval}, 1fr);`};
  grid-template-rows: max-content;
  height: max-content;
  position: relative;
  width: fit-content;

  ${({ isFull }) => isFull && 'flex: 1;'};
`;

const LeftCell = styled(Cell).attrs({ className: 'daily-left-cell' })`
  flex: none;
  flex-basis: 250px;
  padding: 5px 10px;

  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};

  ${({ $zIndex }) =>
    $zIndex &&
    css`
      z-index: ${$zIndex};
    `};
`;

const Header = styled.div`
  position: sticky;
  top: 0px;
  display: inline-table;
  z-index: 4;

  background: ${({ theme }) => theme.colors.white};
  width: 100%;

  .daily-left-cell {
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const Footer = styled(Header)``;

const Seperator = styled.div`
  background-color: ${({ theme }) => theme.colors.grey3};
  width: 100%;
  height: 1px;
`;

const HourlyDailyView = ({
  isLoading,
  data = [],
  weeklyView,
  openShiftsTotalRows,
  RowRenderer,
  GridBaseRenderer,
  bookingActionsFactory,
  mappedBreakTypes,
  DayRenderer,
  openShifts,
  HeaderRenderer,
  offset,
  mappedJobTypes,
  DayCellRenderer,
  t,
  onRemoveOpenShift,
  onAddOpenShift,
  onDayClick,
  onOpenShiftClick,
  extraColumns = [],
  scrollRef,
  bucketOffset,
  onAmountsCellClick,
  firstColumnHeaderRenderer,
  firstColumnFooterRenderer,
  showLeftRow = true,
  employeesMap = {},
  timeOffsByEmployee,
  generalAvailabilitiesByEmployee,
  employeesData = {},
  interval,
  dragAndDropActionsFactory,
  columns,
  date,
  headerRows = [],
  refetchTimeAvailability,
  isOpenShiftsOpenExpand,
  numberOfHoursBuckets,
  refetchProjects,
  startOfDaysByShiftsAndDefault,
  setSelectedShift,
  setPublishProject,
  footerRows = [],
  mappedLabels,
  shiftsByEmployee,
  gridInterval,
  onAddShift,
  addEmployeeModal,
  bookedOutsideAvailabilityByEmployee,
  onCloseAddEmployeeModal
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(false);
  const [timeAvailabilityModal, setTimeAvailabilityModal] = useState(false);
  const [currentHourBucket, setCurrentHourBucket] = useState(-1);
  const lightView = useRecoilValue(schedulingHourlyLightView);

  const formattingMap = useFormattingMap();

  useInterval(
    () => {
      if (moment().format(API_DATE_FORMAT) === date) {
        const roundedCurrentTime = roundTimeToNearest({
          inputTime: moment(),
          roundTimeInMin: interval
        })?.diff(startOfDaysByShiftsAndDefault, 'minutes');
        setCurrentHourBucket(Math.max(0, Math.floor(roundedCurrentTime / interval)));
      } else {
        setCurrentHourBucket(-1);
      }
    },
    5000,
    true
  );

  const children = useMemo(
    () =>
      data.map((item) => {
        const {
          groupId,
          rows,
          numOfShifts,
          numOfHours,
          hourlyWage
          // ...rest
        } = item;

        return (
          <React.Fragment key={groupId}>
            {(rows && rows.length > 0 ? rows : [{}]).map(({ rowId }) => {
              const generalAvailabilityByDay = generalAvailabilitiesByEmployee[rowId]?.find(
                ({ day }) => day === moment(date, API_DATE_FORMAT).day()
              );

              return (
                <GridRow key={rowId || ''} columns={numberOfHoursBuckets} gridInterval={gridInterval}>
                  {showLeftRow && rowId !== undefined && RowRenderer && (
                    <RowRenderer
                      value={rowId}
                      employeesData={employeesData}
                      employeesMap={employeesMap}
                      onPress={setSelectedEmployee}
                      weeklyView={weeklyView}
                      formattingMap={formattingMap}
                      bookedOutsideAvailabilityByEmployee={bookedOutsideAvailabilityByEmployee[rowId]}
                      onClickAvailability={setTimeAvailabilityModal}
                      numOfShifts={numOfShifts}
                      numOfHours={numOfHours}
                      hourlyWage={hourlyWage}
                    />
                  )}

                  <GridBaseRenderer
                    DayRenderer={DayRenderer}
                    gridInterval={gridInterval}
                    onAddShift={onAddShift}
                    groupId={groupId}
                    t={t}
                    employeesData={employeesData}
                    rowId={rowId}
                    size={columns?.length * gridInterval}
                    currentHourBucket={currentHourBucket}
                  />

                  {[...Array(columns?.length * gridInterval)].map((_, index) => (
                    <DayCellRenderer
                      key={`day-cell-renderer-${index}`}
                      DayRenderer={DayRenderer}
                      employeesData={employeesData}
                      bucketOffset={bucketOffset}
                      dragAndDropActionsFactory={dragAndDropActionsFactory}
                      date={date}
                      t={t}
                      index={index}
                      onAddShift={onAddShift}
                      gridInterval={gridInterval}
                      employeeId={rowId}
                      currentHourBucket={currentHourBucket}
                    />
                  ))}
                  {(shiftsByEmployee[rowId] || []).map((shifts, index) => (
                    <BookedRenderer
                      data={shifts}
                      mappedBreakTypes={mappedBreakTypes}
                      gridInterval={gridInterval}
                      maxBucket={numberOfHoursBuckets * gridInterval + 2}
                      date={date}
                      employeeId={rowId}
                      onDayClick={onDayClick}
                      index={index}
                      bookingActionsFactory={bookingActionsFactory}
                      mappedLabels={mappedLabels}
                      offset={offset}
                      t={t}
                      bookedOutsideAvailabilityByEmployee={bookedOutsideAvailabilityByEmployee[rowId]}
                      employeesMap={employeesMap}
                      key={`booked-${groupId}-${rowId}-${index}`}
                    />
                  ))}
                  {(shiftsByEmployee[rowId] || []).map((shifts, index) => (
                    <BreakPlotsRenderer
                      index={index}
                      data={shifts}
                      employeeId={rowId}
                      offset={offset}
                      onDayClick={onDayClick}
                      mappedBreakTypes={mappedBreakTypes}
                      maxBucket={numberOfHoursBuckets * gridInterval + 2}
                      key={`break-plots-${groupId}-${rowId}-${index}`}
                    />
                  ))}
                  {!lightView && (
                    <>
                      {(timeOffsByEmployee[rowId]?.[date] || []).map((rowData, index) => (
                        <TimeOffRenderer
                          data={rowData}
                          employeeId={rowId}
                          t={t}
                          mappedJobTypes={mappedJobTypes}
                          onDayClick={onDayClick}
                          employeesMap={employeesMap}
                          key={`timeoff-${groupId}-${rowId}-${index}`}
                        />
                      ))}
                      <GeneralAvailabilityRenderer
                        generalAvailabilityByDay={generalAvailabilityByDay}
                        offset={offset}
                      />
                    </>
                  )}
                </GridRow>
              );
            })}
          </React.Fragment>
        );
      }),
    [
      data,
      generalAvailabilitiesByEmployee,
      numberOfHoursBuckets,
      gridInterval,
      showLeftRow,
      RowRenderer,
      employeesData,
      employeesMap,
      weeklyView,
      formattingMap,
      bookedOutsideAvailabilityByEmployee,
      DayRenderer,
      onAddShift,
      t,
      columns?.length,
      shiftsByEmployee,
      lightView,
      timeOffsByEmployee,
      date,
      offset,
      bucketOffset,
      dragAndDropActionsFactory,
      currentHourBucket,
      mappedBreakTypes,
      onDayClick,
      bookingActionsFactory,
      mappedLabels,
      mappedJobTypes
    ]
  );

  const HoursRow = useCallback(
    (row) => {
      return (
        <>
          {showLeftRow && <LeftCell $zIndex={5}>{row.firstColumnHeaderRenderer}</LeftCell>}

          {extraColumns.map((column, index) => (
            <Cell key={index}>
              <HeaderRenderer {...column} index={index} currentHourBucket={currentHourBucket} />
            </Cell>
          ))}

          {columns.map((column, index) => {
            return (
              <Cell key={index} header>
                <HeaderRenderer {...column} index={index} currentHourBucket={currentHourBucket} />
              </Cell>
            );
          })}
        </>
      );
    },
    [columns, extraColumns, currentHourBucket, showLeftRow]
  );

  const refetchOnAvailabilityChange = useCallback(() => {
    // refetchProjects?.();
    refetchTimeAvailability?.();
  }, [refetchTimeAvailability]);

  return (
    <Wrapper isLoading={isLoading}>
      {isLoading && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
      <Container ref={scrollRef}>
        <Header>
          {headerRows.map((HeaderRow, idx) => (
            <Row key={idx}>
              {HeaderRow === null && <HoursRow firstColumnHeaderRenderer={firstColumnHeaderRenderer?.[idx]} />}
              {HeaderRow !== null && (
                <>
                  {showLeftRow && <LeftCell $zIndex={5}>{firstColumnHeaderRenderer?.[idx]}</LeftCell>}
                  <HeaderRow
                    currentHourBucket={currentHourBucket}
                    gridInterval={gridInterval}
                    openShiftsTotalRows={openShiftsTotalRows}
                    columns={columns}
                    onRemoveOpenShift={onRemoveOpenShift}
                    onAddOpenShift={onAddOpenShift}
                    onOpenShiftClick={onOpenShiftClick}
                    onAddShift={onAddShift}
                    bookingActionsFactory={bookingActionsFactory}
                    extraColumns={extraColumns}
                    setSelectedShift={setSelectedShift}
                    setPublishProject={setPublishProject}
                    showLeftRow={showLeftRow}
                    refetchProjects={refetchProjects}
                    date={date}
                    isOpenShiftsOpenExpand={isOpenShiftsOpenExpand}
                    offset={offset}
                    dragAndDropActionsFactory={dragAndDropActionsFactory}
                    onAmountsCellClick={onAmountsCellClick}
                    employeesData={employeesData}
                    openShifts={openShifts}
                  />
                </>
              )}
            </Row>
          ))}
          <Seperator />
        </Header>

        {children}

        <Footer>
          {footerRows.map((FooterRow, idx) => (
            <Row key={idx}>
              {FooterRow !== null && (
                <>
                  {showLeftRow && <LeftCell $showBottomBorder>{firstColumnFooterRenderer?.[idx]}</LeftCell>}

                  <FooterRow columns={columns} currentHourBucket={currentHourBucket} />
                </>
              )}
            </Row>
          ))}
        </Footer>
      </Container>

      {selectedEmployee && <EmployeeCard defaultData={selectedEmployee} onClose={() => setSelectedEmployee()} />}

      {timeAvailabilityModal && (
        <EmployeeTimeAvailabilityModal
          onClose={() => setTimeAvailabilityModal(false)}
          refetchOnAvailabilityChange={refetchOnAvailabilityChange}
          accountId={timeAvailabilityModal.accountId}
          employee={timeAvailabilityModal.employee}
        />
      )}

      {addEmployeeModal && <EmployeeCard onClose={onCloseAddEmployeeModal} />}
    </Wrapper>
  );
};

export default HourlyDailyView;
